import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { estimateValue } from '../data/loyaltyPrograms';

const STORAGE_KEY = 'kunjoos_loyalty_v1';

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : { programs: [], activity: [] };
  } catch {
    return { programs: [], activity: [] };
  }
}

function saveToStorage(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    console.error('Failed to save loyalty data');
  }
}

const LoyaltyContext = createContext(null);

export function LoyaltyProvider({ children }) {
  const [data, setData] = useState(() => loadFromStorage());

  const update = useCallback((updater) => {
    setData((prev) => {
      const next = updater(prev);
      saveToStorage(next);
      return next;
    });
  }, []);

  // ── Programs ──────────────────────────────────────────────────────────────

  const enrollProgram = useCallback(
    (programId, { balance, qualifyingUnits, tier, memberNumber = '' }) => {
      update((prev) => ({
        ...prev,
        programs: [
          ...prev.programs.filter((p) => p.programId !== programId),
          {
            id: `${programId}-${Date.now()}`,
            programId,
            balance: Number(balance) || 0,
            qualifyingUnits: Number(qualifyingUnits) || Number(balance) || 0,
            tier,
            memberNumber,
            enrolledAt: new Date().toISOString(),
            lastUpdated: new Date().toISOString(),
          },
        ],
      }));
    },
    [update]
  );

  const updateProgram = useCallback(
    (programId, fields) => {
      update((prev) => ({
        ...prev,
        programs: prev.programs.map((p) =>
          p.programId === programId
            ? { ...p, ...fields, lastUpdated: new Date().toISOString() }
            : p
        ),
      }));
    },
    [update]
  );

  const removeProgram = useCallback(
    (programId) => {
      update((prev) => ({
        ...prev,
        programs: prev.programs.filter((p) => p.programId !== programId),
        activity: prev.activity.filter((a) => a.programId !== programId),
      }));
    },
    [update]
  );

  // ── Activity ─────────────────────────────────────────────────────────────

  const logActivity = useCallback(
    ({ programId, programName, description, points, unit, type }) => {
      const entry = {
        id: `act-${Date.now()}`,
        programId,
        programName,
        description,
        points: Number(points),
        unit,
        type, // 'earned' | 'redeemed'
        date: new Date().toISOString(),
      };

      update((prev) => {
        // Also update program balance
        const delta = type === 'earned' ? entry.points : -entry.points;
        const programs = prev.programs.map((p) =>
          p.programId === programId
            ? {
                ...p,
                balance: Math.max(0, p.balance + delta),
                lastUpdated: new Date().toISOString(),
              }
            : p
        );
        return {
          programs,
          activity: [entry, ...prev.activity].slice(0, 100), // keep last 100
        };
      });
    },
    [update]
  );

  // ── Derived values ────────────────────────────────────────────────────────

  const totalValue = useMemo(() => {
    return data.programs.reduce((sum, p) => sum + estimateValue(p.programId, p.balance), 0);
  }, [data.programs]);

  // Points earned this calendar month
  const monthlyEarned = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    return data.activity
      .filter((a) => a.type === 'earned' && a.date >= monthStart)
      .reduce((sum, a) => sum + a.points, 0);
  }, [data.activity]);

  return (
    <LoyaltyContext.Provider
      value={{
        programs: data.programs,
        activity: data.activity,
        totalValue,
        monthlyEarned,
        enrollProgram,
        updateProgram,
        removeProgram,
        logActivity,
      }}
    >
      {children}
    </LoyaltyContext.Provider>
  );
}

export function useLoyalty() {
  const ctx = useContext(LoyaltyContext);
  if (!ctx) throw new Error('useLoyalty must be used inside LoyaltyProvider');
  return ctx;
}
